import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import JSZip from 'jszip';
import { supabase } from '../../supabase';
import {
  ensureProjectExtensions,
  getBudgetPercentUsed,
  getProfitHealthVariant,
  PROJECT_STATUS_LABELS,
} from '../../utils/projectUtils';
import { sanitizeStorageFileName } from '../../utils/storageUtils';
import { toUserMessage, runReliableAsync, DEVELLO_API_ORIGIN } from '../../utils/asyncReliability';
import {
  uploadExpenseReceipt,
  WORKSPACE_FILE_ACCEPT,
  validateProjectWorkspaceFile,
} from '../../services/uploadExpenseReceipt';
import { saveProjectExpense } from '../../services/saveProjectExpense';
import { devError, devWarn } from '../../utils/logger';

/** Matches dashboard expense categories except utilities (not used in workspace). */
const WORKSPACE_EXPENSE_CATEGORIES = [
  { value: 'materials', label: 'Materials' },
  { value: 'labour', label: 'Labour' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'permits', label: 'Permits' },
  { value: 'other', label: 'Other' },
];

/** Gallery list filter chips (order: before, site, after after All). */
const GALLERY_VIEW_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'before', label: 'Before' },
  { value: 'site', label: 'Site' },
  { value: 'after', label: 'After' },
];

const VAULT_VIEW_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'blueprint', label: 'Blueprint' },
  { value: 'contract', label: 'Signed contract' },
  { value: 'permit', label: 'Permit' },
  { value: 'other', label: 'Other' },
];

function vaultDocTypeLabel(docType) {
  const raw = docType || 'other';
  const hit = VAULT_VIEW_FILTERS.find((f) => f.value === raw);
  if (!hit || hit.value === 'all') return raw;
  return hit.label;
}

const TABS = [
  {
    id: 'overview',
    label: 'Overview',
    desc: 'Health & timeline',
    icon: (c) => (
      <svg className={c} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    id: 'changeOrders',
    label: 'Change orders',
    desc: 'Extra scope & upsells',
    icon: (c) => (
      <svg className={c} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    ),
  },
  {
    id: 'logs',
    label: 'Site logs',
    desc: 'Daily notes',
    icon: (c) => (
      <svg className={c} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
  },
  {
    id: 'gallery',
    label: 'Photos',
    desc: 'Before / after',
    icon: (c) => (
      <svg className={c} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: 'vault',
    label: 'Vault',
    desc: 'PDFs & permits',
    icon: (c) => (
      <svg className={c} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
  },
  {
    id: 'expenses',
    label: 'Expenses',
    desc: 'Project costs',
    icon: (c) => (
      <svg className={c} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
];

const ProfitHealthBar = ({ percent, variant }) => {
  const w = Math.min(100, percent);
  const barClass =
    variant === 'danger' ? 'bg-red-500 shadow-[0_0_12px_rgba(239,68,68,0.45)]' : variant === 'warn' ? 'bg-amber-400' : 'bg-emerald-500';
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-xs text-slate-500">
        <span>Budget remaining</span>
        <span className={variant === 'danger' ? 'font-semibold text-red-600 tabular-nums' : 'text-slate-700 tabular-nums'}>
          {percent.toFixed(0)}% used
        </span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-slate-200/90 overflow-hidden ring-1 ring-slate-200/60">
        <div className={`h-full rounded-full transition-all duration-500 ${barClass}`} style={{ width: `${w}%` }} />
      </div>
      {variant === 'danger' && (
        <p className="text-xs text-red-600 font-medium">Over 90% of budget used — pause and review.</p>
      )}
    </div>
  );
};

const Panel = ({ title, subtitle, children, className = '' }) => (
  <section className={`project-workspace-panel rounded-2xl border border-slate-200/80 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 ${className}`}>
    {(title || subtitle) && (
      <div className="project-workspace-panel-header px-5 pt-5 pb-3 border-b border-slate-100/90 dark:border-slate-700">
        {title && <h3 className="text-sm font-semibold text-slate-900 tracking-tight dark:text-slate-100">{title}</h3>}
        {subtitle && <p className="text-xs text-slate-500 mt-1 leading-relaxed dark:text-slate-400">{subtitle}</p>}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

function getSitePhotoSortTime(photo) {
  if (photo?.createdAt) {
    const t = new Date(photo.createdAt).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const idPrefix = parseInt(String(photo?.id || '').split('_')[0], 10);
  if (!Number.isNaN(idPrefix) && idPrefix > 1e11) return idPrefix;
  return 0;
}

function sortSitePhotosNewestFirst(photos) {
  return [...photos].sort((a, b) => getSitePhotoSortTime(b) - getSitePhotoSortTime(a));
}

/**
 * Full project workspace: financial tools, site docs, and project-scoped expenses.
 */
const ProjectDetailWorkspace = ({
  user,
  project: projectProp,
  expenses,
  teams = [],
  subcontractors = [],
  isReadOnly = false,
  onClose,
  onPatchProject,
  getProjectTotalExpenses,
  saveExpense,
  deleteExpense,
  // Team member overrides
  permittedTabIds = null,   // array of tab ids to show; null = show all
  sectionPermissions = null, // { daily_logs: {view,edit,delete}, ... } or null for contractor (full access)
  addedByName = null,        // display name of the currently logged-in user (contractor or team member)
  updateExpense = null,      // optional: team member expense update handler
  storageBucket = 'project-files',
  storageRoot = 'users',
  showTeamAccess = true,
}) => {
  const project = useMemo(() => ensureProjectExtensions(projectProp), [projectProp]);
  const [tab, setTab] = useState('overview');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  // Section-level permission helpers for team member portal.
  // When sectionPermissions is null (contractor), all actions are allowed.
  const canEdit     = useCallback((key) => !sectionPermissions || (sectionPermissions[key]?.edit   ?? false), [sectionPermissions]);
  const canDelete   = useCallback((key) => !sectionPermissions || (sectionPermissions[key]?.delete ?? false), [sectionPermissions]);
  const canDownload = !sectionPermissions || (sectionPermissions?.download?.view ?? false);

  // Filter tabs for team members; reset if current tab becomes hidden
  const visibleTabs = useMemo(
    () => (permittedTabIds ? TABS.filter((t) => permittedTabIds.includes(t.id)) : TABS),
    [permittedTabIds]
  );
  useEffect(() => {
    if (permittedTabIds && !permittedTabIds.includes(tab)) {
      const first = visibleTabs[0];
      if (first) setTab(first.id);
    }
  }, [permittedTabIds, tab, visibleTabs]);

  // ── Team Access panel state ──────────────────────────────────
  const [teamPanel, setTeamPanel] = useState({ members: [], permsByMember: {}, loading: false, error: '', saving: {} });

  const TEAM_SECTIONS = [
    { key: 'overview',      label: 'Overview', singleToggle: true },
    { key: 'change_orders', label: 'Change Orders' },
    { key: 'daily_logs',    label: 'Site Logs' },
    { key: 'photos',        label: 'Photos' },
    { key: 'vault',         label: 'Vault' },
    { key: 'expenses',      label: 'Expenses' },
    { key: 'download',      label: 'Download Project', singleToggle: true },
  ];

  const DEFAULT_PROJECT_PERMS = {
    overview:      { view: false, edit: false, delete: false },
    daily_logs:    { view: false, edit: false, delete: false },
    change_orders: { view: false, edit: false, delete: false },
    photos:        { view: false, edit: false, delete: false },
    vault:         { view: false, edit: false, delete: false },
    expenses:      { view: false, edit: false, delete: false },
  };

  const loadTeamData = useCallback(async () => {
    if (isReadOnly) return;
    setTeamPanel((p) => ({ ...p, loading: true, error: '' }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;

      const membersResp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/members`, { headers: { Authorization: `Bearer ${token}` } });
      const membersData = await membersResp.json();
      if (!membersResp.ok) throw new Error(membersData.error || 'Failed to load team members');
      const members = membersData.members || [];

      const permsByMember = {};
      await Promise.all(members.map(async (m) => {
        const pr = await fetch(`${DEVELLO_API_ORIGIN}/api/team/members/${m.id}/permissions`, { headers: { Authorization: `Bearer ${token}` } });
        const pd = await pr.json();
        if (!pr.ok) throw new Error(pd.error || 'Failed to load permissions');
        const found = (pd.permissions || []).find((x) => String(x.project_id) === String(project.id));
        let merged = found ? { ...DEFAULT_PROJECT_PERMS, ...found.permissions } : { ...DEFAULT_PROJECT_PERMS };
        // Legacy rows saved before overview existed: keep overview on when other sections are enabled.
        if (found?.permissions && found.permissions.overview === undefined) {
          const hasOther = Object.entries(DEFAULT_PROJECT_PERMS).some(
            ([key, _]) => key !== 'overview' && found.permissions[key]?.view
          );
          if (hasOther) merged = { ...merged, overview: { view: true, edit: false, delete: false } };
        }
        permsByMember[m.id] = merged;
      }));

      setTeamPanel((p) => ({ ...p, members, permsByMember, loading: false }));
    } catch (e) {
      setTeamPanel((p) => ({ ...p, loading: false, error: e.message }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id, isReadOnly]);

  useEffect(() => {
    if (tab === 'team') loadTeamData();
  }, [tab, loadTeamData]);

  const toggleTeamPerm = (memberId, section, action) => {
    setTeamPanel((prev) => {
      const cur = prev.permsByMember[memberId]?.[section] || { view: false, edit: false, delete: false };
      const next = { ...cur, [action]: !cur[action] };
      if (action === 'view' && !next.view) { next.edit = false; next.delete = false; }
      if (action === 'edit' && next.edit) next.view = true;
      if (action === 'delete' && next.delete) { next.view = true; next.edit = true; }
      return { ...prev, permsByMember: { ...prev.permsByMember, [memberId]: { ...prev.permsByMember[memberId], [section]: next } } };
    });
  };

  const saveTeamPerms = async (memberId) => {
    setTeamPanel((p) => ({ ...p, saving: { ...p.saving, [memberId]: true } }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const resp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/members/${memberId}/permissions/${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ permissions: teamPanel.permsByMember[memberId] }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to save permissions');
      setTeamPanel((p) => ({ ...p, saving: { ...p.saving, [memberId]: false } }));
    } catch (e) {
      alert(e.message);
      setTeamPanel((p) => ({ ...p, saving: { ...p.saving, [memberId]: false } }));
    }
  };

  const totalSpent = getProjectTotalExpenses(project.id);
  const budget = parseFloat(project.budget) || 0;
  const pct = getBudgetPercentUsed(budget, totalSpent);
  const healthVariant = getProfitHealthVariant(pct);
  const coTotal = useMemo(
    () => project.changeOrders.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0),
    [project.changeOrders]
  );

  const [coForm, setCoForm] = useState({ title: '', description: '', amount: '', date: '' });
  const [logForm, setLogForm] = useState({ logDate: '', notes: '' });
  const [galleryCaption, setGalleryCaption] = useState('');
  const [galleryTag, setGalleryTag] = useState('site');
  const [galleryListFilter, setGalleryListFilter] = useState('all');
  const [vaultListFilter, setVaultListFilter] = useState('all');
  const [vaultForm, setVaultForm] = useState({ name: '', docType: 'blueprint' });

  // Inline edit state — null = not editing; object = draft values for that item
  const [editingCO,    setEditingCO]    = useState(null); // { id, title, description, amount, date }
  const [editingLog,   setEditingLog]   = useState(null); // { id, logDate, notes }
  const [editingPhoto, setEditingPhoto] = useState(null); // { id, photoTag, caption }
  const [editingVault, setEditingVault] = useState(null); // { id, name, docType }
  const [editingExp,   setEditingExp]   = useState(null); // { id, title, amount, category, date, description }
  // Edit history expand/collapse — Set of item IDs with expanded history
  const [expandedHistory, setExpandedHistory] = useState(new Set());
  const toggleHistory = (id) => setExpandedHistory((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  /** { source: 'photo' | 'vault' | 'receipt', item } */
  const [filePreview, setFilePreview] = useState(null);
  const [expSearch, setExpSearch] = useState('');
  const [expCat, setExpCat] = useState('all');
  const [expForm, setExpForm] = useState({
    title: '',
    amount: '',
    category: '',
    date: new Date().toISOString().slice(0, 10),
    description: '',
    laborOrigin: 'internal',
  });
  /** Saved upload (public URL) after a successful storage write; drives persistence on save. */
  const [receiptAttachment, setReceiptAttachment] = useState(null);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
  /** Filename for the in-progress pick (blob URLs have no extension for preview heuristics). */
  const [receiptPickFileName, setReceiptPickFileName] = useState('');
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptUploadNotice, setReceiptUploadNotice] = useState(null);
  const [savingExpense, setSavingExpense] = useState(false);
  const [deletingExpenseId, setDeletingExpenseId] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadSelections, setDownloadSelections] = useState({ photos: true, vault: true, expenses: true });
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');
  const blockReadOnlyAction = () => {
    alert('This project is read-only on Free. Upgrade to Pro to edit.');
  };

  const projectExpenses = useMemo(() => expenses.filter((e) => e.projectId === project.id), [expenses, project.id]);

  const filterCategoryValues = useMemo(() => {
    const allowed = new Set(WORKSPACE_EXPENSE_CATEGORIES.map((c) => c.value));
    const extra = [
      ...new Set(
        projectExpenses
          .map((e) => (e.category === 'labor' ? 'labour' : e.category))
          .filter((c) => c && c !== 'utilities' && !allowed.has(c))
      ),
    ];
    return ['all', ...WORKSPACE_EXPENSE_CATEGORIES.map((c) => c.value), ...extra];
  }, [projectExpenses]);

  useEffect(() => {
    if (!filterCategoryValues.includes(expCat)) setExpCat('all');
  }, [filterCategoryValues, expCat]);

  useEffect(() => {
    return () => {
      if (receiptPreviewUrl && receiptPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(receiptPreviewUrl);
      }
    };
  }, [receiptPreviewUrl]);

  const filteredExpenses = useMemo(() => {
    const q = expSearch.trim().toLowerCase();
    return projectExpenses.filter((e) => {
      const cat = (e.category === 'labor' ? 'labour' : e.category) || '';
      if (cat === 'utilities') return false;
      const matchQ =
        !q ||
        (e.title && e.title.toLowerCase().includes(q)) ||
        (e.description && e.description.toLowerCase().includes(q));
      const matchC = expCat === 'all' || cat.toLowerCase() === expCat.toLowerCase();
      return matchQ && matchC;
    });
  }, [projectExpenses, expSearch, expCat]);

  const categoryLabel = (value) => {
    const v = value === 'labor' ? 'labour' : value;
    return WORKSPACE_EXPENSE_CATEGORIES.find((c) => c.value === v)?.label || v || 'Other';
  };

  /** Project expenses rolled up by category (utilities excluded), for overview. */
  const spendByCategoryRows = useMemo(() => {
    const totals = new Map();
    for (const e of projectExpenses) {
      const raw = (e.category === 'labor' ? 'labour' : e.category) || 'other';
      if (raw === 'utilities') continue;
      const amt = parseFloat(e.amount) || 0;
      totals.set(raw, (totals.get(raw) || 0) + amt);
    }
    return Array.from(totals.entries())
      .map(([key, amount]) => {
        const v = key === 'labor' ? 'labour' : key;
        const label = WORKSPACE_EXPENSE_CATEGORIES.find((c) => c.value === v)?.label || v || 'Other';
        return { key, amount, label };
      })
      .filter((row) => row.amount > 0)
      .sort((a, b) => b.amount - a.amount);
  }, [projectExpenses]);

  const spendByCategoryTotal = useMemo(
    () => spendByCategoryRows.reduce((s, r) => s + r.amount, 0),
    [spendByCategoryRows]
  );

  const filteredGalleryPhotos = useMemo(() => {
    const list = Array.isArray(project.sitePhotos) ? project.sitePhotos : [];
    const filtered =
      galleryListFilter === 'all'
        ? list
        : list.filter((ph) => {
            const tag = String(ph.photoTag || 'site').toLowerCase();
            return tag === galleryListFilter;
          });
    return sortSitePhotosNewestFirst(filtered);
  }, [project.sitePhotos, galleryListFilter]);

  const filteredVaultDocuments = useMemo(() => {
    const list = Array.isArray(project.vaultDocuments) ? project.vaultDocuments : [];
    if (vaultListFilter === 'all') return list;
    return list.filter((d) => (d.docType || 'other') === vaultListFilter);
  }, [project.vaultDocuments, vaultListFilter]);

  const clearReceiptAttachment = () => {
    setReceiptAttachment(null);
    setReceiptPickFileName('');
    setReceiptUploadNotice(null);
    setReceiptPreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleReceiptFileChange = async (e) => {
    if (isReadOnly) {
      blockReadOnlyAction();
      e.target.value = '';
      return;
    }
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user?.id) return;

    const validated = validateProjectWorkspaceFile(file);
    if (!validated.ok) {
      setReceiptUploadNotice(validated.userMessage);
      return;
    }

    setReceiptPickFileName(file.name || '');
    setReceiptUploadNotice(null);
    setUploadingReceipt(true);
    setReceiptPreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });

    try {
      const uploadResult = await runReliableAsync({
        name: 'receiptUpload',
        timeoutMs: 45000,
        retries: 0,
        fn: () =>
          uploadExpenseReceipt(supabase, {
            userId: user.id,
            projectId: project.id,
            file,
          }),
      });

      if (!uploadResult.ok) {
        clearReceiptAttachment();
        setReceiptUploadNotice(uploadResult.userMessage);
        return;
      }

      setReceiptAttachment(uploadResult.data);
    } catch (err) {
      clearReceiptAttachment();
      setReceiptUploadNotice(
        toUserMessage(err, 'We could not upload that receipt. You can try again or save without a file.')
      );
    } finally {
      setUploadingReceipt(false);
    }
  };

  const addWorkspaceExpense = async () => {
    if (isReadOnly) {
      blockReadOnlyAction();
      return;
    }
    if (!saveExpense) return;
    const resolvedTitle = expForm.title.trim();
    if (!resolvedTitle || !expForm.amount || !expForm.category || !expForm.date) return;
    setSavingExpense(true);
    try {
      const receipts = receiptAttachment ? [receiptAttachment] : [];
      const ok = await saveProjectExpense(saveExpense, {
        title: resolvedTitle,
        amount: expForm.amount,
        category: expForm.category,
        date: expForm.date,
        description: expForm.description.trim(),
        projectId: project.id,
        receipts,
        laborOrigin: expForm.category === 'labour' ? expForm.laborOrigin : 'internal',
        ...(addedByName ? { addedByName } : {}),
      });
      if (ok) {
        setExpForm({
          title: '',
          amount: '',
          category: '',
          date: new Date().toISOString().slice(0, 10),
          description: '',
          laborOrigin: 'internal',
        });
        clearReceiptAttachment();
      }
    } finally {
      setSavingExpense(false);
    }
  };

  const workspaceExpenseSaveDisabled =
    savingExpense ||
    uploadingReceipt ||
    !saveExpense ||
    !expForm.title.trim() ||
    !expForm.amount ||
    !expForm.category ||
    !expForm.date;

  const handleDeleteWorkspaceExpense = async (expenseId) => {
    if (isReadOnly) {
      blockReadOnlyAction();
      return;
    }
    if (!deleteExpense || !expenseId) return;
    if (!window.confirm('Delete this expense? This cannot be undone.')) return;
    setDeletingExpenseId(expenseId);
    try {
      await deleteExpense(expenseId);
    } finally {
      setDeletingExpenseId(null);
    }
  };

  const st = project.projectStatus || 'active';
  const statusLabel = PROJECT_STATUS_LABELS[st] || st;

  const uploadFile = async (file, folder) => {
    if (!file || !user?.id) return null;
    const safeName = sanitizeStorageFileName(file.name);
    const path = `${storageRoot}/${user.id}/projects/${project.id}/${folder}/${Date.now()}_${safeName}`;
    const { error } = await supabase.storage.from(storageBucket).upload(path, file, { upsert: true });
    if (error) throw error;
    const { data: { publicUrl } } = supabase.storage.from(storageBucket).getPublicUrl(path);
    return { url: publicUrl, storagePath: path };
  };

  const handlePhotoUpload = async (e) => {
    if (isReadOnly) {
      blockReadOnlyAction();
      e.target.value = '';
      return;
    }
    const files = Array.from(e.target.files || []);
    if (!files.length || !user?.id) return;

    if (files.length > 10) {
      alert(`You can upload a maximum of 10 photos at once. You selected ${files.length}. Please select 10 or fewer.`);
      e.target.value = '';
      return;
    }

    const invalidFiles = files.map((f) => ({ file: f, result: validateProjectWorkspaceFile(f) })).filter((x) => !x.result.ok);
    if (invalidFiles.length) {
      alert(`${invalidFiles.length} file(s) could not be uploaded:\n${invalidFiles.map((x) => `• ${x.file.name}: ${x.result.userMessage}`).join('\n')}`);
      e.target.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    const newRows = [];
    const errors = [];

    for (let i = 0; i < files.length; i++) {
      setUploadProgress({ current: i + 1, total: files.length });
      try {
        const up = await uploadFile(files[i], 'photos');
        if (up) {
          newRows.push({
            id: `${Date.now()}_${i}`,
            url: up.url,
            storagePath: up.storagePath,
            photoTag: galleryTag,
            caption: galleryCaption.trim() || '',
            createdAt: new Date().toISOString(),
            ...(addedByName ? { addedByName } : {}),
          });
        }
      } catch (err) {
        devError('Photo upload failed:', err);
        errors.push(files[i].name);
      }
    }

    if (newRows.length) {
      onPatchProject(project.id, (p) => ({
        ...ensureProjectExtensions(p),
        sitePhotos: [...newRows, ...(ensureProjectExtensions(p).sitePhotos || [])],
      }));
      setGalleryCaption('');
    }

    if (errors.length) {
      alert(`${errors.length} file(s) failed to upload:\n${errors.map((n) => `• ${n}`).join('\n')}`);
    }

    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    e.target.value = '';
  };

  const handleVaultUpload = async (e) => {
    if (isReadOnly) {
      blockReadOnlyAction();
      e.target.value = '';
      return;
    }
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    const validated = validateProjectWorkspaceFile(file);
    if (!validated.ok) {
      alert(validated.userMessage);
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const up = await uploadFile(file, 'vault');
      if (!up) return;
      const row = {
        id: Date.now().toString(),
        name: vaultForm.name.trim() || file.name,
        docType: vaultForm.docType,
        url: up.url,
        storagePath: up.storagePath,
        createdAt: new Date().toISOString(),
        ...(addedByName ? { addedByName } : {}),
      };
      onPatchProject(project.id, (p) => ({
        ...ensureProjectExtensions(p),
        vaultDocuments: [...(ensureProjectExtensions(p).vaultDocuments || []), row],
      }));
      setVaultForm({ name: '', docType: 'blueprint' });
    } catch (err) {
      devError('Vault upload failed:', err);
      const msg =
        err?.statusCode === 403 || err?.message?.includes('policy')
          ? 'Upload denied. Check your Supabase Storage bucket policies.'
          : /CORS|network|failed/i.test(String(err?.message || ''))
            ? 'Upload blocked — check Supabase Storage CORS or network settings.'
            : err?.message || 'Upload failed.';
      alert(msg);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const addChangeOrder = () => {
    if (isReadOnly) {
      blockReadOnlyAction();
      return;
    }
    if (!coForm.title.trim()) return;
    const row = {
      id: Date.now().toString(),
      title: coForm.title.trim(),
      description: coForm.description.trim(),
      amount: parseFloat(coForm.amount) || 0,
      date: coForm.date || new Date().toISOString().slice(0, 10),
      createdAt: new Date().toISOString(),
      ...(addedByName ? { addedByName } : {}),
    };
    onPatchProject(project.id, (p) => ({
      ...ensureProjectExtensions(p),
      changeOrders: [...ensureProjectExtensions(p).changeOrders, row],
    }));
    setCoForm({ title: '', description: '', amount: '', date: '' });
  };

  const addDailyLog = () => {
    if (isReadOnly) {
      blockReadOnlyAction();
      return;
    }
    if (!logForm.notes.trim() || !logForm.logDate) return;
    const row = {
      id: Date.now().toString(),
      logDate: logForm.logDate,
      notes: logForm.notes.trim(),
      createdAt: new Date().toISOString(),
      ...(addedByName ? { addedByName } : {}),
    };
    onPatchProject(project.id, (p) => ({
      ...ensureProjectExtensions(p),
      dailySiteLogs: [...ensureProjectExtensions(p).dailySiteLogs, row],
    }));
    setLogForm({ logDate: '', notes: '' });
  };

  const deleteChangeOrder = (co) => {
    if (isReadOnly) {
      blockReadOnlyAction();
      return;
    }
    if (!window.confirm('Delete this change order?')) return;
    onPatchProject(project.id, (p) => {
      const ext = ensureProjectExtensions(p);
      return {
        ...ext,
        changeOrders: ext.changeOrders.filter((c) => (co.id ? c.id !== co.id : c !== co)),
      };
    });
  };

  const deleteDailyLog = (log) => {
    if (isReadOnly) {
      blockReadOnlyAction();
      return;
    }
    if (!window.confirm('Delete this site log entry?')) return;
    onPatchProject(project.id, (p) => {
      const ext = ensureProjectExtensions(p);
      return {
        ...ext,
        dailySiteLogs: ext.dailySiteLogs.filter((l) => (log.id ? l.id !== log.id : l !== log)),
      };
    });
  };

  const makeHistEntry = () => ({ at: new Date().toISOString(), by: addedByName || 'Unknown' });

  const saveEditCO = () => {
    if (!editingCO) return;
    const hist = makeHistEntry();
    onPatchProject(project.id, (p) => {
      const ext = ensureProjectExtensions(p);
      return {
        ...ext,
        changeOrders: ext.changeOrders.map((c) =>
          String(c.id) === String(editingCO.id)
            ? { ...c, title: editingCO.title.trim(), description: editingCO.description.trim(), amount: parseFloat(editingCO.amount) || 0, date: editingCO.date, editHistory: [...(c.editHistory || []), hist] }
            : c
        ),
      };
    });
    setEditingCO(null);
  };

  const saveEditLog = () => {
    if (!editingLog) return;
    const hist = makeHistEntry();
    onPatchProject(project.id, (p) => {
      const ext = ensureProjectExtensions(p);
      return {
        ...ext,
        dailySiteLogs: ext.dailySiteLogs.map((l) =>
          String(l.id) === String(editingLog.id)
            ? { ...l, logDate: editingLog.logDate, notes: editingLog.notes.trim(), editHistory: [...(l.editHistory || []), hist] }
            : l
        ),
      };
    });
    setEditingLog(null);
  };

  const saveEditPhoto = () => {
    if (!editingPhoto) return;
    const hist = makeHistEntry();
    onPatchProject(project.id, (p) => {
      const ext = ensureProjectExtensions(p);
      return {
        ...ext,
        sitePhotos: (ext.sitePhotos || []).map((ph) =>
          String(ph.id) === String(editingPhoto.id)
            ? { ...ph, photoTag: editingPhoto.photoTag, caption: editingPhoto.caption.trim(), editHistory: [...(ph.editHistory || []), hist] }
            : ph
        ),
      };
    });
    setEditingPhoto(null);
  };

  const saveEditVault = () => {
    if (!editingVault) return;
    const hist = makeHistEntry();
    onPatchProject(project.id, (p) => {
      const ext = ensureProjectExtensions(p);
      return {
        ...ext,
        vaultDocuments: (ext.vaultDocuments || []).map((d) =>
          String(d.id) === String(editingVault.id)
            ? { ...d, name: editingVault.name.trim() || d.name, docType: editingVault.docType, editHistory: [...(d.editHistory || []), hist] }
            : d
        ),
      };
    });
    setEditingVault(null);
  };

  const saveEditExp = async () => {
    if (!editingExp) return;
    const hist = makeHistEntry();
    const updates = {
      title: editingExp.title.trim(),
      amount: parseFloat(editingExp.amount) || 0,
      category: editingExp.category,
      date: editingExp.date,
      description: editingExp.description.trim(),
    };

    if (updateExpense) {
      // Team member path — must run before saveExpense (team dashboard passes both).
      const original = expenses.find((e) => String(e.id) === String(editingExp.id));
      await updateExpense({
        ...(original || {}),
        ...updates,
        id: editingExp.id,
        editHistory: [...((original?.editHistory) || []), hist],
      });
    } else if (saveExpense) {
      // Contractor path: saveExpense updates profiles.expenses when id is present.
      const original = expenses.find((e) => String(e.id) === String(editingExp.id));
      if (original) {
        await saveExpense(
          { ...original, ...updates, editHistory: [...(original.editHistory || []), hist] },
          { skipUiReset: true }
        );
      }
    } else {
      // Fallback: patch project JSONB locally
      onPatchProject(project.id, (p) => {
        const exp = (p.expenses || []).map((e) =>
          String(e.id) === String(editingExp.id)
            ? { ...e, ...updates, editHistory: [...(e.editHistory || []), hist] }
            : e
        );
        return { ...ensureProjectExtensions(p), expenses: exp };
      });
    }
    setEditingExp(null);
  };

  const removeStorageObject = async (storagePath) => {
    if (!storagePath) return;
    try {
      const { error } = await supabase.storage.from(storageBucket).remove([storagePath]);
      if (error) devWarn('Storage remove:', error.message);
    } catch (e) {
      devWarn('Storage remove failed', e);
    }
  };

  const downloadFromUrl = async (url, filename) => {
    if (!url) return;
    const safeName = sanitizeStorageFileName(filename || 'download');
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = safeName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const deleteSitePhoto = async (ph) => {
    if (isReadOnly) {
      blockReadOnlyAction();
      return;
    }
    if (!window.confirm('Delete this photo? It will be removed from storage.')) return;
    setFilePreview((prev) => (prev?.source === 'photo' && prev.item === ph ? null : prev));
    await removeStorageObject(ph.storagePath);
    onPatchProject(project.id, (p) => {
      const ext = ensureProjectExtensions(p);
      return {
        ...ext,
        sitePhotos: ext.sitePhotos.filter((x) => (ph.id ? x.id !== ph.id : x !== ph)),
      };
    });
  };

  const deleteVaultDocument = async (doc) => {
    if (isReadOnly) {
      blockReadOnlyAction();
      return;
    }
    if (!window.confirm('Delete this file from the vault? It will be removed from storage.')) return;
    setFilePreview((prev) => (prev?.source === 'vault' && prev.item === doc ? null : prev));
    await removeStorageObject(doc.storagePath);
    onPatchProject(project.id, (p) => {
      const ext = ensureProjectExtensions(p);
      return {
        ...ext,
        vaultDocuments: ext.vaultDocuments.filter((x) => (doc.id ? x.id !== doc.id : x !== doc)),
      };
    });
  };

  /** Opens the same full-screen preview modal used for photos and vault files. */
  const openExpenseReceiptPreview = ({ url, storagePath, title, fileName }) => {
    if (!url) return;
    setFilePreview({
      source: 'receipt',
      item: {
        url,
        storagePath: storagePath || '',
        title: title || '',
        fileName: fileName || '',
      },
    });
  };

  const isImageUrlOrName = (name, url) => {
    const base = name || '';
    const path = (url || '').split(/[?#]/)[0];
    return (
      /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif|tiff?)$/i.test(base) ||
      /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif|tiff?)$/i.test(path)
    );
  };

  const fileLabelFromStoragePath = (storagePath) =>
    (storagePath && storagePath.includes('/') && storagePath.split('/').pop()) || '';

  const isGalleryImageThumb = (ph) =>
    isImageUrlOrName(fileLabelFromStoragePath(ph.storagePath) || ph.caption || 'file', ph.url);

  const VAULT_CATEGORY_FOLDERS = {
    blueprint: 'Blueprints',
    permit: 'Signed-Permits',
    contract: 'Contracts',
    other: 'Other',
  };

  const EXPENSE_CATEGORY_FOLDERS = {
    materials: 'Materials',
    labour: 'Labour',
    equipment: 'Equipment',
    transportation: 'Transportation',
    permits: 'Permits',
    other: 'Other',
  };

  const handleDownloadProject = async () => {
    if (!Object.values(downloadSelections).some(Boolean)) return;
    setIsDownloading(true);
    setDownloadProgress('Building archive…');
    const zip = new JSZip();
    const safeProjectName = (project.name || 'Project').replace(/[\\/:*?"<>|]/g, '-');
    const root = zip.folder(safeProjectName);
    try {
      if (downloadSelections.photos) {
        const photosRoot = root.folder('Photos');
        const subFolders = {
          before: photosRoot.folder('Before'),
          after: photosRoot.folder('After'),
          site: photosRoot.folder('Site-General'),
        };
        const photos = Array.isArray(project.sitePhotos) ? project.sitePhotos : [];
        setDownloadProgress(`Fetching ${photos.length} photo(s)…`);
        for (const ph of photos) {
          if (!ph.url) continue;
          try {
            const res = await fetch(ph.url, { mode: 'cors' });
            const blob = await res.blob();
            const rawName = fileLabelFromStoragePath(ph.storagePath) || `photo-${ph.id || Date.now()}.jpg`;
            const folder = subFolders[(ph.photoTag || 'site').toLowerCase()] || subFolders.site;
            folder.file(rawName, blob);
          } catch { /* skip unfetchable file */ }
        }
      }

      if (downloadSelections.vault) {
        const vaultRoot = root.folder('Vault');
        const vaultFolderCache = {};
        const getVaultFolder = (docType) => {
          const key = (docType || 'other').toLowerCase();
          const folderName = VAULT_CATEGORY_FOLDERS[key] || 'Other';
          if (!vaultFolderCache[folderName]) vaultFolderCache[folderName] = vaultRoot.folder(folderName);
          return vaultFolderCache[folderName];
        };
        const docs = Array.isArray(project.vaultDocuments) ? project.vaultDocuments : [];
        setDownloadProgress(`Fetching ${docs.length} vault document(s)…`);
        for (const doc of docs) {
          if (!doc.url) continue;
          try {
            const res = await fetch(doc.url, { mode: 'cors' });
            const blob = await res.blob();
            const rawName = fileLabelFromStoragePath(doc.storagePath) || `${sanitizeStorageFileName(doc.name || 'document')}`;
            getVaultFolder(doc.docType).file(rawName, blob);
          } catch { /* skip unfetchable file */ }
        }
      }

      if (downloadSelections.expenses) {
        const expRoot = root.folder('Expenses');
        const expFolderCache = {};
        const getExpFolder = (category) => {
          const cat = (category === 'labor' ? 'labour' : category) || 'other';
          const folderName = EXPENSE_CATEGORY_FOLDERS[cat] || (cat.charAt(0).toUpperCase() + cat.slice(1));
          if (!expFolderCache[folderName]) expFolderCache[folderName] = expRoot.folder(folderName);
          return expFolderCache[folderName];
        };
        const receiptsToFetch = projectExpenses.flatMap((ex) => {
          const cat = (ex.category === 'labor' ? 'labour' : ex.category) || 'other';
          if (cat === 'utilities') return [];
          const receipts = Array.isArray(ex.receipts) ? ex.receipts : [];
          return receipts
            .filter((r) => r && typeof r === 'object' && r.url)
            .map((r) => ({ url: r.url, storagePath: r.storagePath || '', category: ex.category, title: ex.title }));
        });
        setDownloadProgress(`Fetching ${receiptsToFetch.length} receipt(s)…`);
        for (const receipt of receiptsToFetch) {
          if (!receipt.url) continue;
          try {
            const res = await fetch(receipt.url, { mode: 'cors' });
            const blob = await res.blob();
            const rawName = fileLabelFromStoragePath(receipt.storagePath) || `receipt-${Date.now()}`;
            getExpFolder(receipt.category).file(rawName, blob);
          } catch { /* skip unfetchable file */ }
        }
      }

      setDownloadProgress('Compressing…');
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${safeProjectName}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      setShowDownloadModal(false);
    } catch (err) {
      devError('Project download failed:', err);
      alert('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
      setDownloadProgress('');
    }
  };

  const tabNav = (compact) => (
    <nav
      className={
        compact
          ? 'flex gap-1.5 shrink-0'
          : 'hidden md:flex flex-col gap-0.5 p-3'
      }
      aria-label="Workspace sections"
    >
      {visibleTabs.map((t) => {
        const active = tab === t.id;
        const iconClass = compact
          ? active
            ? 'w-4 h-4 shrink-0 text-white'
            : 'w-4 h-4 shrink-0 text-slate-500'
          : active
            ? 'w-5 h-5 shrink-0 text-primary'
            : 'w-5 h-5 shrink-0 text-slate-400';
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={
              compact
                ? `flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-medium whitespace-nowrap transition-all ${
                    active
                      ? 'bg-primary text-white shadow-md ring-2 ring-primary/30'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                  }`
                : `flex items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-all ${
                    active ? 'bg-primary/10 text-primary ring-1 ring-primary/20' : 'text-slate-600 hover:bg-slate-50'
                  }`
            }
          >
            {t.icon(iconClass)}
            {!compact && (
              <span className="min-w-0">
                <span className="block text-sm font-medium leading-tight">{t.label}</span>
                <span className="block text-[11px] text-slate-500 mt-0.5 leading-snug">{t.desc}</span>
              </span>
            )}
            {compact && <span>{t.label}</span>}
          </button>
        );
      })}
    </nav>
  );

  // ── Edit history widget ──────────────────────────────────────────
  const EditHistory = ({ item }) => {
    const hist = item?.editHistory;
    if (!hist?.length) return null;
    const open = expandedHistory.has(item.id);
    return (
      <div className="mt-2">
        <button
          type="button"
          onClick={() => toggleHistory(item.id)}
          className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
        >
          <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {hist.length} edit{hist.length > 1 ? 's' : ''}
          <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {open && (
          <div className="mt-1.5 space-y-1 border-l-2 border-slate-100 pl-3">
            {[...hist].reverse().map((h, i) => (
              <p key={i} className="text-[11px] text-slate-400">
                Edited by{' '}
                <span className="font-medium text-slate-600">{h.by || 'Unknown'}</span>
                {' · '}
                {new Date(h.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                {' at '}
                {new Date(h.at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </p>
            ))}
          </div>
        )}
      </div>
    );
  };

  const previewTitle =
    filePreview?.source === 'photo'
      ? filePreview.item.caption?.trim() || 'Photo'
      : filePreview?.source === 'vault'
        ? filePreview.item.name || 'Document'
        : filePreview?.source === 'receipt'
          ? filePreview.item.title?.trim()
            ? `${filePreview.item.title} — Receipt`
            : 'Receipt'
          : '';
  const previewUrl = filePreview?.item?.url || '';
  const photoPreviewLabel =
    filePreview?.source === 'photo'
      ? fileLabelFromStoragePath(filePreview.item.storagePath) || filePreview.item.caption || 'photo'
      : '';
  const receiptPreviewLabel =
    filePreview?.source === 'receipt'
      ? fileLabelFromStoragePath(filePreview.item.storagePath) ||
        filePreview.item.fileName ||
        'receipt'
      : '';
  const previewIsImage =
    filePreview &&
    ((filePreview.source === 'photo' && isImageUrlOrName(photoPreviewLabel, filePreview.item.url)) ||
      (filePreview.source === 'vault' && isImageUrlOrName(filePreview.item.name, filePreview.item.url)) ||
      (filePreview.source === 'receipt' &&
        isImageUrlOrName(receiptPreviewLabel, filePreview.item.url)));

  return createPortal(
    <>
    <div className="fixed inset-0 z-[100000] min-h-[100dvh]">
      {/* Portal to body so scrim stacks above app Navbar (z-50) and covers the full viewport */}
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/75 backdrop-blur-[3px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="pointer-events-none absolute inset-0 flex items-stretch justify-center px-2 pt-[max(16px,env(safe-area-inset-top,0px))] pb-[max(16px,env(safe-area-inset-bottom,0px))] sm:px-4 sm:pt-5 sm:pb-5">
        <div className="project-workspace-shell pointer-events-auto flex h-full min-h-0 w-full max-w-[min(100vw-1rem,1600px)] flex-col overflow-hidden rounded-2xl border border-slate-300/90 bg-slate-100/95 shadow-[0_25px_80px_-12px_rgba(15,23,42,0.45)] ring-1 ring-black/10 dark:border-slate-700 dark:bg-slate-950/95 md:flex-row">
        {/* Sidebar — desktop */}
        <aside className="project-workspace-surface hidden md:flex w-[260px] shrink-0 flex flex-col border-r border-slate-200/90 bg-white dark:border-slate-700 dark:bg-slate-900">
          <div className="p-5 border-b border-slate-100 dark:border-slate-700">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Workspace</p>
            <h2 className="mt-1 text-lg font-bold text-slate-900 leading-snug line-clamp-2">{project.name}</h2>
            <p className="text-sm text-slate-500 mt-1 truncate">{project.client || 'No client linked'}</p>
            <span className="inline-flex mt-3 items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700">
              {statusLabel}
            </span>
          </div>
          {tabNav(false)}
          {!isReadOnly && showTeamAccess && (
            <div className="px-3 pt-1 border-t border-slate-100 mt-1">
              <button
                type="button"
                onClick={() => setTab(tab === 'team' ? 'overview' : 'team')}
                className={`flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-all ${
                  tab === 'team' ? 'bg-primary/10 text-primary ring-1 ring-primary/20' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <svg className={`w-5 h-5 shrink-0 mt-0.5 ${tab === 'team' ? 'text-primary' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="min-w-0">
                  <span className="block text-sm font-medium leading-tight">Team Access</span>
                  <span className="block text-[11px] text-slate-500 mt-0.5 leading-snug">Member permissions</span>
                </span>
              </button>
            </div>
          )}
          {canDownload && (
            <div className="mt-auto p-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowDownloadModal(true)}
                className="flex w-full items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:border-primary/40 hover:bg-primary/5 hover:text-primary transition-colors"
              >
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download project
              </button>
            </div>
          )}
        </aside>

        {/* Main column */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* Title bar: mobile always; desktop when sidebar hidden (e.g. expense feed) */}
          <header className="project-workspace-surface flex items-start justify-between gap-3 border-b border-slate-200/90 bg-white px-4 py-3 shrink-0 md:hidden dark:border-slate-700 dark:bg-slate-900">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Project</p>
              <h2 className="text-base font-bold text-slate-900 truncate">{project.name}</h2>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canDownload && (
                <button
                  type="button"
                  onClick={() => setShowDownloadModal(true)}
                  className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-primary/5 hover:text-primary hover:border-primary/30 transition-colors"
                  aria-label="Download project"
                  title="Download project"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 rounded-xl p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </header>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 px-1 -mx-1 scrollbar-thin shrink-0 md:hidden">
            {tabNav(true)}
            {!isReadOnly && showTeamAccess && (
              <button
                type="button"
                onClick={() => setTab(tab === 'team' ? 'overview' : 'team')}
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  tab === 'team'
                    ? 'bg-primary text-white shadow-md ring-2 ring-primary/30'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                }`}
              >
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Team Access
              </button>
            )}
          </div>

          <div className="project-workspace-surface hidden md:flex shrink-0 items-center justify-end border-b border-slate-200/90 bg-white px-8 py-2.5 dark:border-slate-700 dark:bg-slate-900">
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-xl p-2 text-slate-500 hover:bg-slate-100 border border-transparent hover:border-slate-200"
              aria-label="Close workspace"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-5 md:px-8 md:py-8 space-y-6">
            {tab === 'team' && (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-slate-900 tracking-tight">Team Access</h2>
                    <div className="relative group/tip">
                      <button
                        type="button"
                        className="flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-400 hover:border-primary hover:text-primary transition-colors text-[11px] font-bold leading-none"
                        aria-label="How team access works"
                      >
                        i
                      </button>
                      <div className="pointer-events-none absolute left-6 top-0 z-50 w-72 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150">
                        <p className="text-xs font-bold text-slate-800 mb-2">How team access works</p>
                        <ul className="space-y-2 text-xs text-slate-600">
                          <li className="flex gap-2">
                            <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">V</span>
                            <span><span className="font-semibold text-slate-800">View</span> — can read the section but cannot add or delete anything.</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">A</span>
                            <span><span className="font-semibold text-slate-800">Add</span> — can view and add new entries. Automatically enables View.</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-red-50 flex items-center justify-center text-[10px] font-bold text-red-500">D</span>
                            <span><span className="font-semibold text-slate-800">Delete</span> — full access: view, add, and delete entries. Automatically enables View + Add.</span>
                          </li>
                        </ul>
                        <p className="mt-3 text-[11px] text-slate-400">Turning off View also removes Add and Delete for that section.</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Control which team members can access this project and what they can do in each section.</p>
                </div>
                {teamPanel.loading ? (
                  <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : teamPanel.error ? (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{teamPanel.error}</div>
                ) : teamPanel.members.length === 0 ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center">
                    <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-slate-700">No team members yet</p>
                    <p className="text-xs text-slate-500 mt-1">Go to Settings → Team Members to invite your first team member.</p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {teamPanel.members.map((member) => {
                      const perms = teamPanel.permsByMember[member.id] || {};
                      const isSaving = teamPanel.saving[member.id];
                      const hasAnyAccess = Object.values(perms).some((s) => s.view);
                      return (
                        <Panel
                          key={member.id}
                          title={
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-cyan-400 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                                {(member.full_name || member.email)[0].toUpperCase()}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-900">{member.full_name || member.email}</span>
                                <span className="ml-2 text-xs text-slate-500">{member.email}</span>
                              </div>
                              <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${member.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                {member.status}
                              </span>
                            </div>
                          }
                          subtitle={hasAnyAccess ? 'Has access to this project — configure permissions below.' : 'No access to this project yet — enable View on any section to grant access.'}
                        >
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr>
                                  <th className="text-left text-xs font-semibold text-slate-500 pb-3 w-36 uppercase tracking-wide">Section</th>
                                  {['View', 'Add', 'Delete'].map((a) => (
                                    <th key={a} className="text-center text-xs font-semibold text-slate-500 pb-3 w-24 uppercase tracking-wide">{a}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {TEAM_SECTIONS.map(({ key, label, singleToggle }) => {
                                  const p = perms[key] || { view: false, edit: false, delete: false };
                                  if (singleToggle) {
                                    return (
                                      <tr key={key} className="group">
                                        <td className="py-3 text-sm text-slate-800 font-medium">{label}</td>
                                        <td className="text-center py-3">
                                          <button
                                            type="button"
                                            onClick={() => toggleTeamPerm(member.id, key, 'view')}
                                            className={`w-9 h-5 rounded-full transition-colors relative focus:outline-none focus:ring-2 focus:ring-primary/30 ${p.view ? 'bg-primary' : 'bg-slate-200'}`}
                                            role="switch"
                                            aria-checked={p.view}
                                          >
                                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${p.view ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                          </button>
                                        </td>
                                        <td colSpan={2} className="py-3 text-center text-xs text-slate-400 italic">—</td>
                                      </tr>
                                    );
                                  }
                                  return (
                                    <tr key={key} className="group">
                                      <td className="py-3 text-sm text-slate-800 font-medium">{label}</td>
                                      {['view', 'edit', 'delete'].map((action) => (
                                        <td key={action} className="text-center py-3">
                                          <button
                                            type="button"
                                            onClick={() => toggleTeamPerm(member.id, key, action)}
                                            className={`w-9 h-5 rounded-full transition-colors relative focus:outline-none focus:ring-2 focus:ring-primary/30 ${p[action] ? 'bg-primary' : 'bg-slate-200'}`}
                                            role="switch"
                                            aria-checked={p[action]}
                                          >
                                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${p[action] ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                          </button>
                                        </td>
                                      ))}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <div className="mt-4 flex justify-end">
                            <button
                              onClick={() => saveTeamPerms(member.id)}
                              disabled={isSaving}
                              className="px-4 py-2 text-sm font-semibold rounded-xl bg-primary text-white hover:bg-primary-dark disabled:opacity-50 transition-colors"
                            >
                              {isSaving ? 'Saving…' : 'Save permissions'}
                            </button>
                          </div>
                        </Panel>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {tab === 'overview' && (
              <>
                <div className="hidden md:flex flex-col gap-1">
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight">Financial snapshot</h2>
                  <p className="text-sm text-slate-500 mt-0.5">Budget, spend by category, and key dates at a glance.</p>
                </div>

                <Panel title="Profit health" subtitle="Live spend against your estimated budget.">
                  {budget > 0 ? (
                    <>
                      <ProfitHealthBar percent={pct} variant={healthVariant} />
                      <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
                        <div className="rounded-xl bg-slate-50/90 px-4 py-3 ring-1 ring-slate-100">
                          <p className="text-xs text-slate-500">Estimated budget</p>
                          <p className="text-lg font-semibold text-slate-900 tabular-nums">
                            ${budget.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </p>
                        </div>
                        <div className="rounded-xl bg-slate-50/90 px-4 py-3 ring-1 ring-slate-100">
                          <p className="text-xs text-slate-500">Total expenses</p>
                          <p className="text-lg font-semibold text-slate-900 tabular-nums">
                            ${totalSpent.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-6 text-center">
                      <p className="text-sm text-slate-600">No budget set for this project.</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Total recorded spend:{' '}
                        <span className="font-semibold text-slate-800 tabular-nums">
                          ${totalSpent.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </p>
                    </div>
                  )}
                  {coTotal > 0 && (
                    <p className="mt-4 text-xs text-slate-500">
                      Change orders logged:{' '}
                      <span className="font-medium text-slate-800 tabular-nums">${coTotal.toFixed(2)}</span>
                    </p>
                  )}
                </Panel>

                <Panel
                  title="Spend by category"
                  subtitle="Where project expenses sit — same categories as the Expenses tab (utilities omitted)."
                >
                  {spendByCategoryRows.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-6 text-center">
                      <p className="text-sm text-slate-600">No expenses recorded for this project yet.</p>
                      <p className="text-xs text-slate-500 mt-1">Add entries under the Expenses tab to see a breakdown.</p>
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      {spendByCategoryRows.map((row) => {
                        const pctBar =
                          spendByCategoryTotal > 0 ? Math.round((row.amount / spendByCategoryTotal) * 100) : 0;
                        return (
                          <li key={row.key}>
                            <div className="flex items-baseline justify-between gap-3 text-sm">
                              <span className="font-medium text-slate-800 truncate">{row.label}</span>
                              <span className="shrink-0 tabular-nums font-semibold text-slate-900">
                                ${row.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                              <div
                                className="h-full rounded-full bg-primary/80"
                                style={{ width: `${pctBar}%` }}
                              />
                            </div>
                            <p className="mt-0.5 text-[11px] text-slate-400 tabular-nums">{pctBar}% of project spend</p>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </Panel>

                <Panel title="Timeline">
                  <div className="flex flex-wrap gap-8 text-sm">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-400">Start</p>
                      <p className="mt-1 font-medium text-slate-900">
                        {project.startDate ? new Date(project.startDate).toLocaleDateString() : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-400">End</p>
                      <p className="mt-1 font-medium text-slate-900">
                        {project.deadline ? new Date(project.deadline).toLocaleDateString() : '—'}
                      </p>
                    </div>
                  </div>
                </Panel>
              </>
            )}

            {tab === 'changeOrders' && (
              <>
                <div className="md:flex md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Change orders</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Capture extra work so scope creep stays profitable.</p>
                  </div>
                </div>
                {canEdit('change_orders') && (
                  <Panel title="Log new change order">
                    <div className="space-y-3">
                      <input
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm placeholder:text-slate-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                        placeholder="Title *"
                        value={coForm.title}
                        onChange={(e) => setCoForm((f) => ({ ...f, title: e.target.value }))}
                      />
                      <textarea
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm placeholder:text-slate-400 focus:ring-2 focus:ring-primary/30 outline-none"
                        rows={2}
                        placeholder="What changed?"
                        value={coForm.description}
                        onChange={(e) => setCoForm((f) => ({ ...f, description: e.target.value }))}
                      />
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="number"
                          className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                          placeholder="Amount ($)"
                          value={coForm.amount}
                          onChange={(e) => setCoForm((f) => ({ ...f, amount: e.target.value }))}
                        />
                        <input
                          type="date"
                          className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                          value={coForm.date}
                          onChange={(e) => setCoForm((f) => ({ ...f, date: e.target.value }))}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={addChangeOrder}
                        className="w-full rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-primary-dark"
                      >
                        Add change order
                      </button>
                    </div>
                  </Panel>
                )}
                <div className="space-y-3">
                  {project.changeOrders.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-12 text-center text-sm text-slate-500">
                      No change orders yet.
                    </div>
                  )}
                  {project.changeOrders.map((co) => (
                    <div
                      key={co.id || co.title + co.date}
                      className="group rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
                    >
                      {editingCO?.id === co.id ? (
                        <div className="space-y-3">
                          <input
                            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                            placeholder="Title"
                            value={editingCO.title}
                            onChange={(e) => setEditingCO((d) => ({ ...d, title: e.target.value }))}
                          />
                          <textarea
                            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                            rows={3}
                            placeholder="Description"
                            value={editingCO.description}
                            onChange={(e) => setEditingCO((d) => ({ ...d, description: e.target.value }))}
                          />
                          <div className="flex gap-2">
                            <input
                              type="number"
                              className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                              placeholder="Amount"
                              value={editingCO.amount}
                              onChange={(e) => setEditingCO((d) => ({ ...d, amount: e.target.value }))}
                            />
                            <input
                              type="date"
                              className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                              value={editingCO.date}
                              onChange={(e) => setEditingCO((d) => ({ ...d, date: e.target.value }))}
                            />
                          </div>
                          <div className="flex gap-2">
                            <button type="button" onClick={saveEditCO} className="flex-1 rounded-xl bg-primary py-2 text-sm font-semibold text-white hover:bg-primary-dark">Save</button>
                            <button type="button" onClick={() => setEditingCO(null)} className="flex-1 rounded-xl border border-slate-200 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between gap-3 items-start">
                            <h4 className="font-semibold text-slate-900 min-w-0 pr-2">{co.title}</h4>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-sm font-bold text-emerald-700 tabular-nums">
                                ${(co.amount || 0).toFixed(2)}
                              </span>
                              {canEdit('change_orders') && (
                                <button
                                  type="button"
                                  title="Edit change order"
                                  onClick={() => setEditingCO({ id: co.id, title: co.title || '', description: co.description || '', amount: String(co.amount ?? ''), date: co.date || '' })}
                                  className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 hover:text-primary hover:border-primary/30 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                              {canDelete('change_orders') && (
                                <button
                                  type="button"
                                  title="Delete change order"
                                  onClick={() => deleteChangeOrder(co)}
                                  className="rounded-xl border border-red-200 bg-white p-2 text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                          {co.description && <p className="text-sm text-slate-600 mt-2 leading-relaxed">{co.description}</p>}
                          <div className="flex items-center gap-3 mt-3">
                            <p className="text-[11px] text-slate-400">{co.date}</p>
                            {co.addedByName && (
                              <span className="text-[11px] text-slate-400">· Added by <span className="font-medium text-slate-600">{co.addedByName}</span></span>
                            )}
                          </div>
                          <EditHistory item={co} />
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {tab === 'logs' && (
              <>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Daily site logs</h2>
                  <p className="text-sm text-slate-500 mt-0.5">A simple paper trail for weather, delays, and progress.</p>
                </div>
                {canEdit('daily_logs') && (
                  <Panel title="New entry">
                    <div className="space-y-3">
                      <input
                        type="date"
                        className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                        value={logForm.logDate}
                        onChange={(e) => setLogForm((f) => ({ ...f, logDate: e.target.value }))}
                      />
                      <textarea
                        className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                        rows={5}
                        placeholder="e.g. Poured concrete at 9 AM. Rain at 1 PM — stopped work."
                        value={logForm.notes}
                        onChange={(e) => setLogForm((f) => ({ ...f, notes: e.target.value }))}
                      />
                      <button
                        type="button"
                        onClick={addDailyLog}
                        className="w-full rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-primary-dark"
                      >
                        Save log
                      </button>
                    </div>
                  </Panel>
                )}
                <div className="space-y-3">
                  {[...project.dailySiteLogs]
                    .sort((a, b) => (b.logDate || b.date || '').localeCompare(a.logDate || a.date || ''))
                    .map((log) => (
                      <article
                        key={log.id || `${log.logDate}-${(log.notes || '').slice(0, 20)}`}
                        className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm"
                      >
                        {editingLog?.id === log.id ? (
                          <div className="space-y-3">
                            <input
                              type="date"
                              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                              value={editingLog.logDate}
                              onChange={(e) => setEditingLog((d) => ({ ...d, logDate: e.target.value }))}
                            />
                            <textarea
                              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                              rows={5}
                              value={editingLog.notes}
                              onChange={(e) => setEditingLog((d) => ({ ...d, notes: e.target.value }))}
                            />
                            <div className="flex gap-2">
                              <button type="button" onClick={saveEditLog} className="flex-1 rounded-xl bg-primary py-2 text-sm font-semibold text-white hover:bg-primary-dark">Save</button>
                              <button type="button" onClick={() => setEditingLog(null)} className="flex-1 rounded-xl border border-slate-200 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex flex-wrap items-center gap-2">
                                <time className="text-xs font-semibold text-primary">{log.logDate || log.date}</time>
                                {log.addedByName && (
                                  <span className="text-[11px] text-slate-400">· <span className="font-medium text-slate-600">{log.addedByName}</span></span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {canEdit('daily_logs') && (
                                  <button
                                    type="button"
                                    title="Edit log entry"
                                    onClick={() => setEditingLog({ id: log.id, logDate: log.logDate || log.date || '', notes: log.notes || '' })}
                                    className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 hover:text-primary hover:border-primary/30 transition-colors"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                  </button>
                                )}
                                {canDelete('daily_logs') && (
                                  <button
                                    type="button"
                                    title="Delete log entry"
                                    onClick={() => deleteDailyLog(log)}
                                    className="rounded-xl border border-red-200 bg-white p-2 text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-slate-800 whitespace-pre-wrap mt-2 leading-relaxed">{log.notes}</p>
                            <EditHistory item={log} />
                          </>
                        )}
                      </article>
                    ))}
                </div>
              </>
            )}

            {tab === 'gallery' && (
              <>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Before &amp; after</h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Tag as before, after, or site — upload images, PDFs, or other job files; click an item to preview or
                    download.
                  </p>
                </div>
                {canEdit('photos') && (
                  <Panel title="Upload">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <select
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
                        value={galleryTag}
                        onChange={(e) => setGalleryTag(e.target.value)}
                      >
                        <option value="before">Before</option>
                        <option value="after">After</option>
                        <option value="site">Site / general</option>
                      </select>
                    </div>
                    <input
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm mb-3"
                      placeholder="Caption (optional)"
                      value={galleryCaption}
                      onChange={(e) => setGalleryCaption(e.target.value)}
                    />
                    <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 py-10 hover:bg-slate-50 transition-colors">
                      <input
                        type="file"
                        accept={WORKSPACE_FILE_ACCEPT}
                        className="hidden"
                        multiple
                        onChange={handlePhotoUpload}
                        disabled={uploading}
                      />
                      <svg className="w-10 h-10 text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm font-medium text-slate-600">
                        {uploading
                          ? uploadProgress.total > 1
                            ? `Uploading ${uploadProgress.current} of ${uploadProgress.total}…`
                            : 'Uploading…'
                          : 'Drop files here or tap to browse'}
                      </span>
                      {!uploading && (
                        <span className="text-xs text-slate-400 mt-1">Select up to 10 photos at once</span>
                      )}
                    </label>
                  </Panel>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-slate-500 shrink-0">Show:</span>
                  {GALLERY_VIEW_FILTERS.map((f) => {
                    const active = galleryListFilter === f.value;
                    return (
                      <button
                        key={f.value}
                        type="button"
                        onClick={() => setGalleryListFilter(f.value)}
                        className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors border ${
                          active
                            ? 'bg-primary text-white border-primary shadow-sm'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
                {filteredGalleryPhotos.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-10 text-center text-sm text-slate-500">
                    {galleryListFilter === 'all'
                      ? 'No photos yet — upload above.'
                      : `No items tagged “${GALLERY_VIEW_FILTERS.find((x) => x.value === galleryListFilter)?.label || galleryListFilter}”.`}
                  </div>
                ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
                  {filteredGalleryPhotos.map((ph) => (
                    <div
                      key={ph.id}
                      className="group min-w-0 overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm"
                    >
                      <button
                        type="button"
                        onClick={() => setFilePreview({ source: 'photo', item: ph })}
                        className="block w-full min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                      >
                        <div className="aspect-square overflow-hidden bg-slate-100 cursor-pointer sm:aspect-[4/3]">
                          {isGalleryImageThumb(ph) ? (
                            <img
                              src={ph.url}
                              alt=""
                              className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
                            />
                          ) : (
                            <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-2 sm:gap-2 sm:p-4 text-center">
                              <svg
                                className="h-8 w-8 sm:h-12 sm:w-12 shrink-0 text-slate-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                />
                              </svg>
                              <span className="text-xs font-medium text-slate-600 line-clamp-2">
                                {fileLabelFromStoragePath(ph.storagePath) || 'File'}
                              </span>
                              <span className="text-[10px] text-slate-400">Tap to open</span>
                            </div>
                          )}
                        </div>
                      </button>
                      {editingPhoto?.id === ph.id ? (
                        <div className="p-2 space-y-2">
                          <select
                            className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs bg-white focus:ring-2 focus:ring-primary/30 outline-none"
                            value={editingPhoto.photoTag}
                            onChange={(e) => setEditingPhoto((d) => ({ ...d, photoTag: e.target.value }))}
                          >
                            <option value="before">Before</option>
                            <option value="after">After</option>
                            <option value="site">Site / general</option>
                          </select>
                          <input
                            className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs focus:ring-2 focus:ring-primary/30 outline-none"
                            placeholder="Caption"
                            value={editingPhoto.caption}
                            onChange={(e) => setEditingPhoto((d) => ({ ...d, caption: e.target.value }))}
                          />
                          <div className="flex gap-1.5">
                            <button type="button" onClick={saveEditPhoto} className="flex-1 rounded-lg bg-primary py-1.5 text-xs font-semibold text-white hover:bg-primary-dark">Save</button>
                            <button type="button" onClick={() => setEditingPhoto(null)} className="flex-1 rounded-lg border border-slate-200 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-2 flex flex-wrap items-center justify-between gap-1.5 text-[11px] sm:text-xs">
                          <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
                            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 sm:px-2 font-medium text-slate-600 capitalize shrink-0">
                              {ph.photoTag}
                            </span>
                            {ph.caption && (
                              <p className="w-full text-slate-700 text-[11px] sm:text-xs line-clamp-2 leading-snug">{ph.caption}</p>
                            )}
                            {ph.addedByName && (
                              <p className="w-full text-[10px] text-slate-400">by <span className="font-medium text-slate-500">{ph.addedByName}</span></p>
                            )}
                            {ph.editHistory?.length > 0 && (
                              <div className="w-full"><EditHistory item={ph} /></div>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {canEdit('photos') && (
                              <button
                                type="button"
                                title="Edit photo"
                                onClick={(e) => { e.stopPropagation(); setEditingPhoto({ id: ph.id, photoTag: ph.photoTag || 'site', caption: ph.caption || '' }); }}
                                className="rounded-lg border border-slate-200 bg-white p-1.5 sm:p-2 text-slate-500 hover:bg-slate-50 hover:text-primary hover:border-primary/30 transition-colors"
                              >
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                            )}
                            {canDelete('photos') && (
                              <button
                                type="button"
                                title="Delete photo"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteSitePhoto(ph);
                                }}
                                className="rounded-lg border border-red-200 bg-white p-1.5 sm:p-2 text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                )}
              </>
            )}

            {tab === 'vault' && (
              <>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Document vault</h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Blueprints, permits, contracts, photos, PDFs, and other job documents in one place.
                  </p>
                </div>
                {canEdit('vault') && (
                  <Panel title="Upload">
                    <div className="space-y-3">
                      <input
                        className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                        placeholder="Document name"
                        value={vaultForm.name}
                        onChange={(e) => setVaultForm((f) => ({ ...f, name: e.target.value }))}
                      />
                      <select
                        className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white"
                        value={vaultForm.docType}
                        onChange={(e) => setVaultForm((f) => ({ ...f, docType: e.target.value }))}
                      >
                        <option value="blueprint">Blueprint</option>
                        <option value="permit">Permit</option>
                        <option value="contract">Signed contract</option>
                        <option value="other">Other</option>
                      </select>
                      <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 py-8 hover:bg-slate-50">
                        <input
                          type="file"
                          accept={WORKSPACE_FILE_ACCEPT}
                          className="hidden"
                          onChange={handleVaultUpload}
                          disabled={uploading}
                        />
                        <span className="text-sm text-slate-600">
                          {uploading ? 'Uploading…' : 'Images, PDF, Office, zip, video/audio — up to 50 MB'}
                        </span>
                      </label>
                    </div>
                  </Panel>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-slate-500 shrink-0">Show:</span>
                  {VAULT_VIEW_FILTERS.map((f) => {
                    const active = vaultListFilter === f.value;
                    return (
                      <button
                        key={f.value}
                        type="button"
                        onClick={() => setVaultListFilter(f.value)}
                        className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors border ${
                          active
                            ? 'bg-primary text-white border-primary shadow-sm'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
                <div className="space-y-2">
                  {filteredVaultDocuments.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-10 text-center text-sm text-slate-500">
                      {vaultListFilter === 'all'
                        ? 'No documents yet — upload above.'
                        : `No files tagged “${vaultDocTypeLabel(vaultListFilter)}”.`}
                    </div>
                  )}
                  {filteredVaultDocuments.map((doc) => {
                    const vaultIsImg = isImageUrlOrName(doc.name, doc.url);
                    return (
                      <div
                        key={doc.id}
                        className="flex gap-3 rounded-2xl border border-slate-200/90 bg-white p-3 text-sm shadow-sm hover:border-primary/30 hover:shadow transition-all"
                      >
                        {vaultIsImg && (
                          <button
                            type="button"
                            onClick={() => setFilePreview({ source: 'vault', item: doc })}
                            className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          >
                            <img src={doc.url} alt="" className="h-full w-full object-cover" />
                          </button>
                        )}
                        {editingVault?.id === doc.id ? (
                          <div className="flex-1 space-y-2">
                            <input
                              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                              placeholder="Document name"
                              value={editingVault.name}
                              onChange={(e) => setEditingVault((d) => ({ ...d, name: e.target.value }))}
                            />
                            <select
                              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary/30 outline-none"
                              value={editingVault.docType}
                              onChange={(e) => setEditingVault((d) => ({ ...d, docType: e.target.value }))}
                            >
                              {['blueprint','permit','contract','invoice','photo','other'].map((t) => (
                                <option key={t} value={t}>{vaultDocTypeLabel(t)}</option>
                              ))}
                            </select>
                            <div className="flex gap-2">
                              <button type="button" onClick={saveEditVault} className="flex-1 rounded-xl bg-primary py-1.5 text-sm font-semibold text-white hover:bg-primary-dark">Save</button>
                              <button type="button" onClick={() => setEditingVault(null)} className="flex-1 rounded-xl border border-slate-200 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <button
                                type="button"
                                onClick={() => setFilePreview({ source: 'vault', item: doc })}
                                className="text-left"
                              >
                                <span className="font-medium text-primary truncate block">{doc.name}</span>
                                <span className="flex items-center gap-2 text-xs text-slate-500">
                                  <span>{vaultDocTypeLabel(doc.docType)}</span>
                                  {doc.addedByName && <span className="text-slate-400">· <span className="text-slate-500">{doc.addedByName}</span></span>}
                                </span>
                              </button>
                              <EditHistory item={doc} />
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0 self-center">
                              {canEdit('vault') && (
                                <button
                                  type="button"
                                  title="Edit document"
                                  onClick={() => setEditingVault({ id: doc.id, name: doc.name || '', docType: doc.docType || 'other' })}
                                  className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 hover:text-primary hover:border-primary/30 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                              {canDelete('vault') && (
                                <button
                                  type="button"
                                  title="Delete file"
                                  onClick={() => deleteVaultDocument(doc)}
                                  className="rounded-xl border border-red-200 bg-white p-2 text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {tab === 'expenses' && (
              <>
                <div className="md:flex md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Expenses</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Add costs for this job and filter what you have recorded.</p>
                  </div>
                </div>

                {canEdit('expenses') && (
                  <Panel title="Add expense">
                  <div className="space-y-3">
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-4">
                      <p className="text-xs font-medium text-slate-700 mb-2">Receipt (optional)</p>
                      <p className="text-xs text-slate-500 mb-3">
                        Attach a receipt or any supporting file (image, PDF, scan, etc.) — up to 50 MB — then fill in
                        total and date below.
                      </p>
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-primary border border-primary/30 shadow-sm hover:bg-primary/5 transition-colors">
                        <input
                          type="file"
                          accept={WORKSPACE_FILE_ACCEPT}
                          className="hidden"
                          disabled={uploadingReceipt || savingExpense || !user?.id}
                          onChange={handleReceiptFileChange}
                        />
                        {uploadingReceipt ? 'Uploading…' : 'Upload receipt'}
                      </label>
                      {uploadingReceipt && (
                        <p className="text-xs text-slate-600 mt-3" aria-live="polite">
                          Uploading receipt…
                        </p>
                      )}
                      {receiptUploadNotice && (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-3" role="status">
                          {receiptUploadNotice}
                        </p>
                      )}
                      {(receiptPreviewUrl || receiptAttachment?.url) && !uploadingReceipt && (
                        <div className="mt-3 flex flex-wrap items-start gap-3">
                          <button
                            type="button"
                            title="View attachment"
                            onClick={() => {
                              const url = receiptAttachment?.url || receiptPreviewUrl;
                              if (!url) return;
                              openExpenseReceiptPreview({
                                url,
                                storagePath: receiptAttachment?.storagePath,
                                title: expForm.title.trim() || 'New expense',
                                fileName: receiptPickFileName,
                              });
                            }}
                            className="h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          >
                            {(() => {
                              const url = receiptAttachment?.url || receiptPreviewUrl || '';
                              const nm =
                                fileLabelFromStoragePath(receiptAttachment?.storagePath) || receiptPickFileName || '';
                              return isImageUrlOrName(nm, url) ? (
                                <img src={url} alt="Attachment preview" className="h-full w-full object-cover" />
                              ) : (
                                <div className="flex h-full w-full flex-col items-center justify-center bg-slate-50 p-1 text-center">
                                  <span className="text-[9px] font-semibold uppercase text-slate-500">File</span>
                                  <span className="mt-0.5 line-clamp-2 text-[9px] text-slate-600 leading-tight">{nm || 'Open'}</span>
                                </div>
                              );
                            })()}
                          </button>
                          <div className="flex flex-col gap-2 min-w-0">
                            {receiptAttachment?.url && (
                              <button
                                type="button"
                                onClick={() =>
                                  openExpenseReceiptPreview({
                                    url: receiptAttachment.url,
                                    storagePath: receiptAttachment.storagePath,
                                    title: expForm.title.trim() || 'New expense',
                                    fileName: receiptPickFileName,
                                  })
                                }
                                className="text-xs font-medium text-primary hover:underline break-all text-left"
                              >
                                View receipt
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={clearReceiptAttachment}
                              className="text-xs text-slate-600 hover:text-slate-900 underline self-start"
                            >
                              Remove receipt
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <input
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm placeholder:text-slate-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                      placeholder="Title *"
                      value={expForm.title}
                      onChange={(e) => setExpForm((f) => ({ ...f, title: e.target.value }))}
                    />
                    <textarea
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm placeholder:text-slate-400 focus:ring-2 focus:ring-primary/30 outline-none"
                      rows={2}
                      placeholder="Notes (optional)"
                      value={expForm.description}
                      onChange={(e) => setExpForm((f) => ({ ...f, description: e.target.value }))}
                    />
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                        placeholder="Total ($) *"
                        value={expForm.amount}
                        onChange={(e) => setExpForm((f) => ({ ...f, amount: e.target.value }))}
                      />
                      <input
                        type="date"
                        className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                        value={expForm.date}
                        onChange={(e) => setExpForm((f) => ({ ...f, date: e.target.value }))}
                      />
                    </div>
                    <select
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                      value={expForm.category}
                      onChange={(e) =>
                        setExpForm((f) => ({
                          ...f,
                          category: e.target.value,
                          laborOrigin: e.target.value === 'labour' ? f.laborOrigin : 'internal',
                        }))
                      }
                    >
                      <option value="">Category *</option>
                      {WORKSPACE_EXPENSE_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                    {expForm.category === 'labour' && (
                      <select
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                        value={expForm.laborOrigin}
                        onChange={(e) => setExpForm((f) => ({ ...f, laborOrigin: e.target.value }))}
                      >
                        <option value="internal">Internal crew</option>
                        <option value="subcontractor">Subcontractor</option>
                      </select>
                    )}
                    <button
                      type="button"
                      disabled={workspaceExpenseSaveDisabled}
                      onClick={addWorkspaceExpense}
                      className="w-full rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingExpense ? 'Saving…' : 'Save expense'}
                    </button>
                  </div>
                </Panel>
                )}

                <Panel title="Filter list">
                  <div className="space-y-3">
                    <div className="relative">
                      <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                        placeholder="Search title, notes…"
                        value={expSearch}
                        onChange={(e) => setExpSearch(e.target.value)}
                      />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-2">Category</p>
                      <div className="flex flex-wrap gap-2">
                        {filterCategoryValues.map((c) => {
                          const active = expCat === c;
                          const label = c === 'all' ? 'All' : categoryLabel(c);
                          return (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setExpCat(c)}
                              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors border ${
                                active
                                  ? 'bg-primary text-white border-primary shadow-sm'
                                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </Panel>

                <div className="space-y-2">
                  {filteredExpenses.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-12 text-center text-sm text-slate-500">
                      No expenses yet, or nothing matches this filter.
                    </div>
                  )}
                  {filteredExpenses.map((ex) => {
                    const rc = (ex.receipts && ex.receipts.length) || ex.receipt ? 1 : 0;
                    const cat = ex.category === 'labor' ? 'labour' : ex.category;
                    const receiptObj =
                      (ex.receipts && ex.receipts.find((r) => r && typeof r === 'object' && r.url)) || null;
                    const receiptUrl = receiptObj?.url || null;
                    const receiptStoragePath = receiptObj?.storagePath || '';
                    return (
                      <div
                        key={ex.id}
                        className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm"
                      >
                        {editingExp?.id === ex.id ? (
                          <div className="space-y-3">
                            <input
                              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                              placeholder="Title"
                              value={editingExp.title}
                              onChange={(e) => setEditingExp((d) => ({ ...d, title: e.target.value }))}
                            />
                            <div className="flex gap-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                                placeholder="Amount ($)"
                                value={editingExp.amount}
                                onChange={(e) => setEditingExp((d) => ({ ...d, amount: e.target.value }))}
                              />
                              <input
                                type="date"
                                className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                                value={editingExp.date}
                                onChange={(e) => setEditingExp((d) => ({ ...d, date: e.target.value }))}
                              />
                            </div>
                            <select
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                              value={editingExp.category}
                              onChange={(e) => setEditingExp((d) => ({ ...d, category: e.target.value }))}
                            >
                              {WORKSPACE_EXPENSE_CATEGORIES.map((c) => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                              ))}
                            </select>
                            <textarea
                              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/30 outline-none"
                              rows={2}
                              placeholder="Description (optional)"
                              value={editingExp.description}
                              onChange={(e) => setEditingExp((d) => ({ ...d, description: e.target.value }))}
                            />
                            <div className="flex gap-2">
                              <button type="button" onClick={saveEditExp} className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-primary-dark">Save</button>
                              <button type="button" onClick={() => setEditingExp(null)} className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-900 truncate">{ex.title}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                <span>{categoryLabel(cat)}</span>
                                <span className="mx-1.5 text-slate-300">·</span>
                                {ex.date || '—'}
                                {ex.addedByName && (
                                  <span className="ml-1.5 text-slate-400">· <span className="font-medium text-slate-600">{ex.addedByName}</span></span>
                                )}
                                {ex.laborOrigin === 'subcontractor' && (
                                  <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800">
                                    Sub labour
                                  </span>
                                )}
                              </p>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                                {rc > 0 && (
                                  <p className="text-xs text-emerald-700">{rc} receipt attachment</p>
                                )}
                                {receiptUrl && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      openExpenseReceiptPreview({
                                        url: receiptUrl,
                                        storagePath: receiptStoragePath,
                                        title: ex.title,
                                        fileName: fileLabelFromStoragePath(receiptStoragePath),
                                      })
                                    }
                                    className="text-xs font-medium text-primary hover:underline"
                                  >
                                    View receipt
                                  </button>
                                )}
                              </div>
                              <EditHistory item={ex} />
                            </div>
                            <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                              <p className="text-lg font-bold text-slate-900 tabular-nums">
                                ${parseFloat(ex.amount || 0).toFixed(2)}
                              </p>
                              {canEdit('expenses') && (
                                <button
                                  type="button"
                                  title="Edit expense"
                                  onClick={() => setEditingExp({ id: ex.id, title: ex.title || '', amount: String(ex.amount ?? ''), category: cat || 'materials', date: ex.date || '', description: ex.description || '' })}
                                  className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 hover:text-primary hover:border-primary/30 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                              {deleteExpense && canDelete('expenses') && (
                                <button
                                  type="button"
                                  title="Delete expense"
                                  disabled={deletingExpenseId === ex.id}
                                  onClick={() => handleDeleteWorkspaceExpense(ex.id)}
                                  className="rounded-xl border border-red-200 bg-white p-2 text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                                >
                                  {deletingExpenseId === ex.id ? (
                                    <span className="text-xs font-medium px-1">…</span>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>

    {showDownloadModal && (
      <div className="fixed inset-0 z-[100200] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Download project">
        <button
          type="button"
          className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
          onClick={() => !isDownloading && setShowDownloadModal(false)}
          aria-label="Close"
        />
        <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-black/10">
          <div className="flex items-center gap-3 mb-1">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-slate-900">Download project</h3>
          </div>
          <p className="text-sm text-slate-500 mb-5 pl-[52px]">
            Files are organized into folders inside a ZIP named after the project.
          </p>
          <div className="space-y-2.5 mb-6">
            {[
              {
                key: 'photos',
                label: 'Photos',
                desc: 'Folders: Before / After / Site-General',
                icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
              },
              {
                key: 'vault',
                label: 'Vault',
                desc: 'Folders: Blueprints / Signed-Permits / Contracts / Other',
                icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
              },
              {
                key: 'expenses',
                label: 'Expenses',
                desc: 'Receipt files per category (Materials, Labour, Equipment…)',
                icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
              },
            ].map(({ key, label, desc, icon }) => (
              <label
                key={key}
                className={`flex items-start gap-3 cursor-pointer rounded-xl border px-4 py-3 transition-colors ${
                  downloadSelections[key]
                    ? 'border-primary/40 bg-primary/5 ring-1 ring-primary/20'
                    : 'border-slate-200 bg-white hover:bg-slate-50'
                } ${isDownloading ? 'pointer-events-none opacity-60' : ''}`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded accent-primary shrink-0"
                  checked={downloadSelections[key]}
                  disabled={isDownloading}
                  onChange={(e) => setDownloadSelections((s) => ({ ...s, [key]: e.target.checked }))}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 shrink-0 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={icon} />
                    </svg>
                    <p className="text-sm font-semibold text-slate-800">{label}</p>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 leading-snug">{desc}</p>
                </div>
              </label>
            ))}
          </div>
          {downloadProgress && (
            <div className="flex items-center gap-2 rounded-xl bg-primary/5 border border-primary/20 px-3 py-2.5 mb-4">
              <svg className="w-4 h-4 text-primary animate-spin shrink-0" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <p className="text-xs font-medium text-primary">{downloadProgress}</p>
            </div>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowDownloadModal(false)}
              disabled={isDownloading}
              className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDownloadProject}
              disabled={isDownloading || !Object.values(downloadSelections).some(Boolean)}
              className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-50 transition-colors"
            >
              {isDownloading ? 'Working…' : 'Download ZIP'}
            </button>
          </div>
        </div>
      </div>
    )}

    {filePreview && (
      <div className="fixed inset-0 z-[100110] flex items-center justify-center p-3 sm:p-6" role="dialog" aria-modal="true" aria-label="File preview">
        <button
          type="button"
          className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
          aria-label="Close preview"
          onClick={() => setFilePreview(null)}
        />
        <div
          className="relative z-10 flex max-h-[min(92vh,920px)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3">
            <h3 className="min-w-0 flex-1 text-sm font-semibold text-slate-900 truncate pr-2">{previewTitle}</h3>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => {
                  const item = filePreview.item;
                  let filename = 'download';
                  if (filePreview.source === 'vault') {
                    filename = item.name || 'document';
                  } else if (filePreview.source === 'photo') {
                    filename = `${item.photoTag || 'photo'}-${item.id || 'image'}`;
                    if (item.storagePath) {
                      const ext = item.storagePath.split('.').pop();
                      if (ext && /^[a-z0-9]+$/i.test(ext) && ext.length <= 5) filename = `${filename}.${ext}`;
                      else filename = `${filename}.jpg`;
                    }
                  } else if (filePreview.source === 'receipt') {
                    const base = sanitizeStorageFileName(item.title || 'receipt');
                    let ext = 'jpg';
                    if (item.storagePath) {
                      const e = item.storagePath.split('.').pop();
                      if (e && /^[a-z0-9]+$/i.test(e) && e.length <= 5) ext = e;
                    }
                    filename = `${base}.${ext}`;
                  }
                  downloadFromUrl(previewUrl, filename);
                }}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
              >
                Download
              </button>
              {filePreview.source !== 'receipt' && (
                <button
                  type="button"
                  onClick={() => {
                    if (filePreview.source === 'photo') deleteSitePhoto(filePreview.item);
                    else deleteVaultDocument(filePreview.item);
                  }}
                  className="rounded-xl border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={() => setFilePreview(null)}
                className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200"
              >
                Close
              </button>
            </div>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-slate-900 p-2 sm:p-4">
            {previewIsImage ? (
              <img
                src={previewUrl}
                alt=""
                className="max-h-[min(78vh,820px)] max-w-full object-contain"
              />
            ) : (
              <iframe
                title={previewTitle}
                src={previewUrl}
                className="h-[min(78vh,820px)] w-full rounded-lg bg-white"
              />
            )}
          </div>
        </div>
      </div>
    )}
    </>,
    document.body
  );
};

export default ProjectDetailWorkspace;
